import ImageLazy from "../../ui/ImageLazy/ImageLazy";
import { richTextSummaryHandler } from "../../content/RichTextSummary/RichTextSummary";
import {
  FaArrowRight,
  FaBookOpen,
  FaCalendarAlt,
  FaFire,
  FaGraduationCap,
  FaTag,
  FaVideo,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import Emoji from "../../ui/Emoji/Emoji";
import { ContentColumn } from "../../layout/ContentColumn/ContentColumn";

interface CourseListProps {
  name: string;
  pathName: string;
  data: any;
  tile?: boolean;
  smallImage?: boolean;
  short?: boolean;
  vertical?: boolean;
  persian?: boolean;
  pageSize?: number;
}

const CourseList = ({ name, pathName, data }: CourseListProps) => {
  const courses = data?.data || [];

  return (
    <div>
      {/* Courses Section */}
      {courses.length === 0 ? (
        <ContentColumn anchor="section-courselist" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-20">
          <div className="bg-white p-12 rounded-smooth-lg border border-border-subtle text-center">
            <Emoji name="bored" size="hero-md" responsive alt="No courses available" />
            <h3 className="font-heading text-h2 font-bold text-brand-navy mt-8 mb-3">
              No Courses Yet
            </h3>
            <p className="font-body text-body text-ink-soft mb-2">No courses available at the moment.</p>
            <p className="font-body text-body-sm text-ink-muted">Check back soon for new courses!</p>
          </div>
        </ContentColumn>
      ) : (
        <ContentColumn anchor="section-courselist" className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col gap-10">
            {courses.map((course: any) => {
              const courseUrl =
                pathName === "/courses"
                  ? `/courses/${course.slug}`
                  : `/${pathName}/${course.slug}`;

              const isPresale = course.is_presale === true;
              const originalPriceRaw =
                course.price !== undefined && course.price !== null
                  ? Number(course.price)
                  : null;
              const presalePriceRaw =
                course.presale_price !== undefined && course.presale_price !== null
                  ? Number(course.presale_price)
                  : null;
              const displayPriceRaw =
                isPresale && presalePriceRaw !== null ? presalePriceRaw : originalPriceRaw;
              const isFree = displayPriceRaw !== null && displayPriceRaw === 0;
              const formattedPrice =
                displayPriceRaw !== null
                  ? displayPriceRaw.toLocaleString("en-US", {
                      minimumFractionDigits: isFree ? 0 : 2,
                      maximumFractionDigits: 2,
                    })
                  : null;

              const publishedLabel = course.publishedAt
                ? new Date(course.publishedAt).toLocaleDateString("en-US", {
                    timeZone: "UTC",
                    month: "short",
                    year: "numeric",
                  })
                : null;

              const summary =
                course.short_description?.substring(0, 220) ||
                (course.content ? richTextSummaryHandler({ content: course.content }).substring(0, 220) : "");

              const imageSrc = course.image?.formats?.medium?.url || course.image?.url;

              return (
                <article key={course.id} className="group w-full">
                  <Link to={courseUrl} className="block w-full">
                    <article className="flex w-full flex-col overflow-hidden rounded-smooth-lg border border-border-subtle bg-white transition-colors duration-150 hover:border-border-strong lg:flex-row">
                      {/* Image Section */}
                      <div className="relative w-full overflow-hidden bg-brand-navy lg:w-[340px] xl:w-[400px] flex-shrink-0">
                        <div className="aspect-[16/10] lg:aspect-[4/3] w-full">
                          {imageSrc ? (
                            <ImageLazy
                              src={imageSrc}
                              alt={course.title}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-brand-navy to-brand-sky p-6 text-center">
                              <FaBookOpen className="text-4xl text-white/70" aria-hidden="true" />
                              <span className="font-heading text-body-sm font-bold uppercase tracking-[0.1em] text-white/90 line-clamp-2">{course.title}</span>
                            </div>
                          )}
                        </div>

                        {/* Labels - positioned bottom-left with solid backgrounds for contrast */}
                        <div className="absolute bottom-0 left-0 right-0 p-4 flex flex-wrap items-end gap-2">
                          <span className="inline-flex items-center gap-1.5 rounded-smooth bg-brand-navy px-2.5 py-1 font-heading text-overline uppercase text-white">
                            <FaGraduationCap className="text-xs" aria-hidden="true" />
                            Course
                          </span>
                          {course.video_url && (
                            <span className="inline-flex items-center gap-1.5 rounded-smooth bg-brand-navy px-2.5 py-1 font-heading text-overline uppercase text-white">
                              <FaVideo className="text-xs" aria-hidden="true" />
                              Video
                            </span>
                          )}
                          {isPresale && (
                            <span className="inline-flex items-center gap-1.5 rounded-smooth bg-brand-coral px-2.5 py-1 font-heading text-overline uppercase text-white">
                              <FaFire className="text-xs" aria-hidden="true" />
                              Presale
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className="flex flex-1 flex-col p-6 lg:p-8">
                        {/* Header: Level + Price */}
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                          {course.level && (
                            <span className="font-heading text-overline uppercase tracking-wider text-ink-muted">
                              {course.level}
                            </span>
                          )}
                          {formattedPrice !== null && (
                            <>
                              {course.level && <span className="text-ink-muted/40">·</span>}
                              <span className={`font-heading text-overline uppercase tracking-wider font-semibold ${isFree ? "text-brand-forest" : "text-brand-burgundy"}`}>
                                {isFree ? "Free" : `$${formattedPrice}`}
                              </span>
                            </>
                          )}
                          {isPresale && originalPriceRaw && (
                            <>
                              <span className="text-ink-muted/40">·</span>
                              <span className="font-heading text-overline uppercase tracking-wider text-brand-red line-through">
                                ${originalPriceRaw.toLocaleString("en-US")}
                              </span>
                            </>
                          )}
                        </div>

                        {/* Title */}
                        <h3 className="font-heading text-h3 font-bold text-brand-navy mb-3">
                          {course.title}
                        </h3>

                        {/* Description */}
                        {summary && (
                          <p className="font-body text-body-sm text-ink-soft line-clamp-2 mb-6">
                            {summary}
                          </p>
                        )}

                        {/* Footer */}
                        <div className="mt-auto flex items-center justify-between pt-4 border-t border-border-subtle">
                          {publishedLabel && (
                            <span className="font-body text-caption text-ink-muted">
                              {publishedLabel}
                            </span>
                          )}
                          <span className="inline-flex items-center gap-2 font-heading text-body-sm font-semibold text-brand-navy transition-transform duration-150 group-hover:translate-x-0.5">
                            Start Course
                            <FaArrowRight className="text-xs" aria-hidden="true" />
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                </article>
              );
            })}
          </div>
        </ContentColumn>
      )}
    </div>
  );
};

export default CourseList;
